// src/pages/Builders/ProjectManagement.jsx
import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "../../layout/builders/Layout";
import LiveTable from "../../layout/builders/ProjectLive/LiveTable";
import HoldTable from "../../layout/builders/ProjectHold/HoldTable";
import RejectionTable from "../../layout/builders/ProjectRejection/RejectionTable";
import CompletionRequestTable from "../../layout/builders/ProjectCompletionRequest/CompletionReqTable";
import CompletedTable from "../../layout/builders/ProjectCompleted/CompletedTable";
import useAuthStore from "../../store/useAuthStore";
import "./ProjectManagement.css";

const TAB_CONFIG = [
    { key: "live", label: "Live Projects" },
    { key: "hold", label: "Hold Projects" },
    { key: "rejected", label: "Rejected" },
    { key: "completion_request", label: "Completion Request" },
    { key: "completed", label: "Completed" },
];

const ProjectManagement = () => {
    const { user } = useAuthStore();
    const isIncharge = user?.role === "incharge";
    const [searchParams, setSearchParams] = useSearchParams();
    const tabFromUrl = searchParams.get("tab") || "live";
    const [activeTab, setActiveTab] = useState(tabFromUrl);

    // Sync tab with URL param
    useEffect(() => {
        const urlTab = searchParams.get("tab") || "live";
        if (urlTab !== activeTab) {
            setActiveTab(urlTab);
        }
    }, [searchParams]);

    const handleTabChange = (key) => {
        setActiveTab(key);
        setSearchParams({ tab: key });
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case "live":
                return <LiveTable />;
            case "hold":
                return <HoldTable />;
            case "rejected":
                return <RejectionTable />;
            case "completion_request":
                return <CompletionRequestTable />;
            case "completed":
                return <CompletedTable />;
            default:
                return <LiveTable />;
        }
    };

    return (
        <div className="min-h-screen bg-gray-100 overflow-x-hidden">
            <Layout>
                <div className="space-y-4 w-full max-w-full px-1 xs:px-2 lg:px-1">
                    {/* Header Card */}
                    <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border w-full max-w-full">
                        <h1 className="text-lg sm:text-xl font-bold text-gray-800">Project Management</h1>
                    </div>

                    {/* Tab Navigation - Admin-style pill tabs */}
                    {!isIncharge && (
                        <div className="bg-white p-2 sm:p-3 rounded-lg shadow-sm border w-full max-w-full overflow-hidden">
                            {/* Mobile (horizontal scroll) */}
                            <div className="flex gap-2 sm:gap-3 overflow-x-auto pb-2 md:hidden scrollbar-hide">
                                {TAB_CONFIG.map((tab) => {
                                    const active = activeTab === tab.key;
                                    return (
                                        <button
                                            key={tab.key}
                                            onClick={() => handleTabChange(tab.key)}
                                            className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full whitespace-nowrap text-xs sm:text-sm flex-shrink-0 ${active
                                                ? "bg-blue-100 text-blue-600 font-semibold"
                                                : "text-gray-600 bg-gray-100"
                                                }`}
                                        >
                                            {tab.label}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Desktop tabs */}
                            <div className="hidden md:flex flex-wrap gap-3">
                                {TAB_CONFIG.map((tab) => {
                                    const active = activeTab === tab.key;
                                    return (
                                        <button
                                            key={tab.key}
                                            onClick={() => handleTabChange(tab.key)}
                                            className={`flex items-center gap-2 px-4 py-2 rounded-full ${active
                                                ? "bg-blue-100 text-blue-600 font-semibold"
                                                : "text-gray-600 hover:bg-gray-100"
                                                }`}
                                        >
                                            {tab.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Tab Content */}
                    <div className="w-full max-w-full overflow-hidden">
                        {renderTabContent()}
                    </div>
                </div>
            </Layout>
        </div>
    );
};

export default ProjectManagement;
